// Connect Slack, one step at a time (#601), in the order #581 Q1 and Q4 settled.
//
// The flow's first step is the part the console explained nowhere: how to get a bot
// token at all, from a ready app manifest (`slackManifest.ts`). After it the flow asks
// for its settings through the field components (`slackFields.tsx`) and the two refusals
// (`slackTokenProblem`, `slackChannelProblem`), so its words cannot drift from the ones
// #578 and #571 pinned.
//
// The state is a pure reducer, as `JiraFlow.tsx`'s is and for the same two claims: going
// back never costs a person what they typed, and a second press of Connect never makes a
// second connection. Saving is the existing create and nothing else (#581 Q3); the test
// message is #614's call through `connectThenTest`, on unless unticked (#581 Q3b).
//
// The choose step lists the workspace's public channels (#604, #581 Q2) with the token the
// sign-in step was given, each marked by whether the app is in it: a channel it is not in
// says how to invite it and checks again, rather than failing at the first delivery. A
// private channel is still given by its ID (`channels:read` lists public ones only), and
// so is any channel when Slack will not list them — a token without `channels:read`, or a
// workspace with more channels than one page holds, which is refused rather than cut.
//
// **Editing a connection is this flow too** (#817), as it is the Jira flow (`JiraFlow.tsx`):
// opened at the choose step with the connection's channel and threshold in place, its
// channels listed by the connection's id with the token the server holds, and the sign-in
// step reached only by choosing to replace the token. A channel the list does not hold — a
// private one — stays, typed. What a save sends is the edit's (`slackEdit`, #580).
//
// This module and `Integrations.tsx` import each other: the panel renders the flow, and
// the flow reads the panel's refusals, which stay in that file because
// `tests/test_integration_setup.py` reads it for their sentences. Both sides use the
// other only inside functions, never while the module loads.

import { useEffect, useReducer, useRef, useState, type FormEvent, type ReactNode } from "react";

import { messageOf } from "../api/http";
import {
  SEVERITY_LABEL,
  type IntegrationRow,
  type NewIntegration,
  type NewSlack,
  type Severity,
  type SlackConfig,
  type SlackPatch,
  type SlackRow,
} from "../api/integrations";
import type { ListedChannels, SlackAsking, SlackChannel, SlackLister } from "../api/slackChannels";
import { reinstallHref, SLACK_INSTALL_PATH, type SlackInstallResult } from "../api/slackInstall";
import { Notice } from "./chrome";
import { failedCheckInWords, grantInWords, missingGrant } from "./connectionHealth";
import {
  reinstallable,
  slackChannelProblem,
  slackChannelSent,
  slackEdit,
  slackIntegration,
  slackTokenProblem,
  type Edit,
} from "./Integrations";
import { JiraChoice } from "./jiraFields";
import {
  alreadyConnected,
  connectThenTest,
  EditingLine,
  NOTHING_CHANGED,
  sameSpaceInWords,
  SavedChanges,
  type SameSpace,
  type TestAsked,
} from "./JiraFlow";
import { APPROVALS_ALWAYS_POST, SLACK_CONNECTED, SlackChannelField, SlackFloorField, SlackTokenField } from "./slackFields";
import { SLACK_APPS, SLACK_MANIFEST_SCOPES, SLACK_MANIFEST_TEXT } from "./slackManifest";
import { StepFlow, stepAfter, stepBefore, type FlowStep } from "./StepFlow";

export type SlackStep = "create-app" | "sign-in" | "where" | "threshold" | "confirm" | "done";

export const SLACK_STEPS: ReadonlyArray<FlowStep<SlackStep>> = [
  { id: "create-app", label: "Create the app" },
  { id: "sign-in", label: "Sign in" },
  { id: "where", label: "Choose where to send" },
  { id: "threshold", label: "Conclusions threshold" },
  { id: "confirm", label: "Confirm" },
  { id: "done", label: "Done" },
];

/** Editing has no app to create: it is already installed (#817). */
export const SLACK_EDIT_STEPS: ReadonlyArray<FlowStep<SlackStep>> = SLACK_STEPS.filter((step) => step.id !== "create-app");

/** Back from Add to Slack (#937): Slack made the app and holds the token, so the flow is the rest. */
export const SLACK_INSTALL_STEPS: ReadonlyArray<FlowStep<SlackStep>> = SLACK_STEPS.filter(
  (step) => step.id !== "create-app" && step.id !== "sign-in",
);

/** An install the callback refused, by its word (#937). */
export type SlackInstallRefusal = Exclude<SlackInstallResult["word"], "installed" | "already_installed" | "replaced">;

/**
 * The workspace's installation as the connections list knows it (#938): its Slack name, when
 * any connection carries it, and how many live connections post through it.
 */
export interface HeldInstallation {
  name: string | null;
  connections: number;
}

/** How many of this workspace's connections post through an installation, in words. */
function throughInWords(connections: number): string {
  if (connections === 0) return "no connection here posts through it yet";
  return connections === 1 ? "1 connection here posts through it" : `${connections} connections here post through it`;
}

/**
 * What a plain Add to Slack of a workspace this one already holds says (#938): nothing was
 * written, and what the explicit replace would change.
 */
export function heldInWords(held: HeldInstallation): string {
  const all = held.connections > 1 ? " for all of them" : held.connections === 1 ? " for it" : "";
  return `Torque Assist is already added to ${held.name ?? "that Slack workspace"}, and ${throughInWords(held.connections)}. Nothing was changed: reinstalling replaces its token and permissions${all}.`;
}

/** What a Reinstall that replaced the installation says (#938). */
export function reinstalledInWords(held: HeldInstallation): string {
  const who =
    held.connections === 0
      ? "no connection posts through it yet"
      : held.connections === 1
        ? "the 1 connection that posts through it uses now"
        : `the ${held.connections} connections that post through it use now`;
  return `Reinstalled ${held.name ?? "the Slack workspace"}: its new token and permissions are what ${who}.`;
}

/** What the flow holds as typed. The floor is empty until a person chooses one. */
export interface SlackEntered {
  token: string;
  channel: string;
  floor: Severity | "";
}

/** The channel list: not asked for, being read, refused (and why), or in hand. */
export type ChannelListing = "idle" | "loading" | { failed: string } | { items: SlackChannel[] };

export interface SlackFlowState {
  step: SlackStep;
  fields: SlackEntered;
  channels: ChannelListing;
  /** The request the list is waiting on: an answer to any other is of a token since changed. */
  asked: number;
  /** A channel's ID typed instead of chosen: a private one, which the list cannot hold. */
  typingId: boolean;
  tokenError: string | null;
  channelError: string | null;
  saving: boolean;
  failure: string | null;
  /** Send a test message once the connection is saved. On unless a person unticks it. */
  sendTest: boolean;
  /** What became of that test message, once the save has landed. */
  test: TestAsked;
  /**
   * The active connection the server named when it refused this one (#762): the workspace
   * already posts to this channel. Set, the next Connect says the second is meant.
   */
  sameSpace: SameSpace | null;
  /** The saved connection this flow edits (#817), or null when it connects a new one. */
  editing: { id: string; config: SlackConfig } | null;
  /** Editing, and the person chose to replace the token: the list is read with the one typed. */
  replacing: boolean;
  /** What an edit's save changed, for its last step. */
  changed: string[] | null;
  /** The workspace's installation Add to Slack made (#937): the connection posts with its token. */
  installation: { id: string } | null;
  /** An Add to Slack the callback refused, said on the first step (#937). */
  refusal: SlackInstallRefusal | null;
  /** An Add to Slack of a workspace this one already holds (#938): that installation, unchanged. */
  held: { id: string } | null;
}

export const openingSlackFlow: SlackFlowState = {
  step: "create-app",
  fields: { token: "", channel: "", floor: "" },
  channels: "idle",
  asked: 0,
  typingId: false,
  tokenError: null,
  channelError: null,
  saving: false,
  failure: null,
  sendTest: true,
  test: "not-asked",
  sameSpace: null,
  editing: null,
  replacing: false,
  changed: null,
  installation: null,
  refusal: null,
  held: null,
};

/** The flow an Edit opens (#817): at the choose step, the connection's channel and threshold in place. */
export function editingSlackFlow(row: { id: string; config: SlackConfig }): SlackFlowState {
  return {
    ...openingSlackFlow,
    step: "where",
    fields: { token: "", channel: row.config.channel, floor: row.config.severity_floor },
    editing: { id: row.id, config: row.config },
  };
}

/**
 * The flow a return from Slack opens (#937): installed, at the choose step with the
 * installation's channels; refused, at the first step saying why; with no result, as
 * Connect Slack opens it.
 */
export function returnedSlackFlow(result: SlackInstallResult | null): SlackFlowState {
  if (result === null) return openingSlackFlow;
  if (result.word === "installed") return { ...openingSlackFlow, step: "where", installation: { id: result.installationId } };
  // Nothing was written: the flow says so, and offers the replace or the installation as it is.
  if (result.word === "already_installed") return { ...openingSlackFlow, held: { id: result.installationId } };
  // A replace is said on the list it changed, not in a flow (`Integrations.tsx`).
  if (result.word === "replaced") return openingSlackFlow;
  return { ...openingSlackFlow, refusal: result.word };
}

/** What a refused install is called, as a person acts on it (#937). */
export const SLACK_INSTALL_REFUSED: Record<SlackInstallRefusal, string> = {
  refused:
    "Slack did not add Torque Assist: the request was declined in Slack, or Slack would not complete it. Nothing was connected. Add to Slack again, or use your own Slack app.",
  expired:
    "That Add to Slack had expired, or was started in another browser session, so nothing was connected. Start it again with Add to Slack.",
  conflict:
    "That Slack workspace is already added to another Torque Assist workspace, so it cannot post here, and nothing was connected. An admin of the other workspace has to remove Torque Assist from that Slack workspace first.",
  other_slack_team:
    "Slack answered for a different workspace from the one being reinstalled, so nothing was changed. Choose Reinstall on that connection again, and pick its workspace in Slack.",
};

export type SlackFlowEvent =
  | { type: "typed"; field: "token" | "channel"; value: string }
  | { type: "floor-chosen"; floor: Severity | "" }
  /** The person left a field: say now what is wrong with it, as the form's blur does. */
  | { type: "left"; field: "token" | "channel" }
  | { type: "continue" }
  | { type: "back" }
  | { type: "listing"; id: number }
  | { type: "channels-listed"; id: number; answer: ListedChannels }
  /** The API's own refusal, or no answer at all. */
  | { type: "listing-failed"; id: number; message: string }
  /** Read the list again: after an /invite, or after Slack refused it. */
  | { type: "recheck" }
  | { type: "channel-chosen"; id: string }
  | { type: "typing-id"; on: boolean }
  | { type: "test-toggled"; on: boolean }
  /** Go on with the installation the workspace already holds, as it is (#938). */
  | { type: "use-held" }
  /** Editing: paste a new token, to list and save with it (#817). */
  | { type: "replace-token" }
  | { type: "saving" }
  | { type: "saved"; test?: TestAsked; changed?: string[] }
  | { type: "refused"; message: string }
  /** Refused because the workspace already posts to this channel (#762). */
  | { type: "already-connected"; existing: SameSpace };

const ERROR_OF = { token: "tokenError", channel: "channelError" } as const;

/** What is wrong with a typed field, in the form's own refusals (#578). */
const problemWith = (field: "token" | "channel", typed: string): string | null =>
  field === "token" ? slackTokenProblem(typed) : slackChannelProblem(typed);

/** The step's one typed field read: held with its refusal, or let past. Empty is `required`'s to refuse. */
const continuedPast = (state: SlackFlowState, field: "token" | "channel", next: SlackStep): SlackFlowState => {
  const problem = problemWith(field, state.fields[field]);
  return problem === null ? { ...state, step: next } : { ...state, [ERROR_OF[field]]: problem };
};

/** The list is in hand and a channel is chosen from it, not typed. */
const choosing = (state: SlackFlowState): state is SlackFlowState & { channels: { items: SlackChannel[] } } =>
  !state.typingId && typeof state.channels === "object" && "items" in state.channels && state.channels.items.length > 0;

/** The channel chosen from the list, or null when none is, or it is typed. */
export function chosenChannel(state: SlackFlowState): SlackChannel | null {
  if (!choosing(state)) return null;
  return state.channels.items.find((channel) => channel.id === state.fields.channel) ?? null;
}

/**
 * Why Slack would not list the channels, as a person acts on it (#604).
 *
 * A token without `channels:read` is not a wrong token, and a workspace with more
 * channels than one page is not an empty one: each says what to do. Anything else is a
 * failed check's words (`connectionHealth.ts`), and the adapter's own where none fit.
 */
export function channelsRefusedInWords(answer: Pick<ListedChannels, "last_status" | "last_detail" | "more">): string {
  if (answer.more) {
    return "This workspace has more public channels than one list can hold, so it is not listed here. Give the channel’s ID instead.";
  }
  if (/\bmissing_scope\b/.test(answer.last_detail)) {
    return "Slack will not list this app’s channels: it was installed without the channels:read permission. Add it by pasting the manifest from the first step into the app’s App Manifest page, reinstall the app, and paste its token again — or give the channel’s ID instead.";
  }
  return failedCheckInWords("slack", { status: "failed", last_status: answer.last_status, last_detail: answer.last_detail, checked_at: "" });
}

export function slackFlowReducer(state: SlackFlowState, event: SlackFlowEvent): SlackFlowState {
  // A save in flight holds the flow still, and a finished flow has nowhere to go back
  // to: either would put a person one press from a second connection.
  if (state.step === "done") return state;
  if (state.saving && event.type !== "saved" && event.type !== "refused" && event.type !== "already-connected") return state;
  switch (event.type) {
    case "typed": {
      const typed = { ...state, fields: { ...state.fields, [event.field]: event.value }, [ERROR_OF[event.field]]: null };
      // A list read with another token is not this one's.
      return event.field === "token" ? { ...typed, channels: "idle" as const } : typed;
    }
    case "floor-chosen":
      return { ...state, fields: { ...state.fields, floor: event.floor } };
    case "left":
      return { ...state, [ERROR_OF[event.field]]: problemWith(event.field, state.fields[event.field]) };
    case "continue":
      if (state.step === "confirm") return state;
      // Another workspace's Slack: there is nothing here to go on with (#937).
      if (state.refusal === "conflict") return state;
      // Replacing a saved token takes a token: an empty field replaces nothing.
      if (state.step === "sign-in" && state.editing !== null && state.fields.token.trim() === "") return state;
      if (state.step === "sign-in") return continuedPast(state, "token", "where");
      if (state.step === "where" && choosing(state)) {
        // Chosen, and the app is in it: a channel it is not in would refuse every post.
        return chosenChannel(state)?.is_member === true ? { ...state, step: "threshold" } : state;
      }
      if (state.step === "where") return continuedPast(state, "channel", "threshold");
      if (state.step === "threshold" && state.fields.floor === "") return state;
      return { ...state, step: stepAfter(SLACK_STEPS, state.step), refusal: null };
    case "back":
      if (state.editing !== null && state.step === "sign-in") {
        // Back from replacing it: the saved token is kept, and the list read with it again.
        return { ...state, step: "where", replacing: false, fields: { ...state.fields, token: "" }, tokenError: null, channels: "idle" };
      }
      // An edit opens here: before it is only the sign-in a person chooses to redo.
      if (state.editing !== null && state.step === "where" && !state.replacing) return state;
      // So does a return from Slack: the app was made there, and there is no token to give.
      if (state.installation !== null && state.step === "where") return state;
      // The warning is about the channel confirmed; going back is to change it.
      return { ...state, step: stepBefore(SLACK_STEPS, state.step), failure: null, sameSpace: null };
    case "listing":
      return { ...state, channels: "loading", asked: event.id };
    case "channels-listed": {
      if (state.channels !== "loading" || event.id !== state.asked) return state;
      if (event.answer.status !== "passed") return { ...state, channels: { failed: channelsRefusedInWords(event.answer) } };
      const items = event.answer.channels;
      const held = state.typingId || items.some((channel) => channel.id === state.fields.channel);
      // An edited connection's channel the list does not hold — a private one — is still its channel: typed, and kept.
      if (!held && state.editing !== null && state.fields.channel !== "") return { ...state, channels: { items }, typingId: true };
      return { ...state, channels: { items }, fields: held ? state.fields : { ...state.fields, channel: "" } };
    }
    case "listing-failed":
      return state.channels === "loading" && event.id === state.asked ? { ...state, channels: { failed: event.message } } : state;
    case "recheck":
      return state.channels === "loading" ? state : { ...state, channels: "idle" };
    case "channel-chosen":
      return choosing(state) ? { ...state, fields: { ...state.fields, channel: event.id }, channelError: null } : state;
    case "typing-id":
      return { ...state, typingId: event.on, fields: { ...state.fields, channel: "" }, channelError: null };
    case "test-toggled":
      return { ...state, sendTest: event.on };
    case "use-held":
      return state.held === null ? state : { ...state, step: "where", installation: state.held, held: null };
    case "replace-token":
      if (state.editing === null || state.step !== "where") return state;
      return { ...state, step: "sign-in", replacing: true, fields: { ...state.fields, token: "" }, channels: "idle" };
    case "saving":
      return state.step === "confirm" ? { ...state, saving: true, failure: null } : state;
    case "already-connected":
      return { ...state, saving: false, failure: null, sameSpace: event.existing };
    case "saved":
      return {
        ...state,
        step: "done",
        saving: false,
        test: event.test ?? "not-asked",
        changed: event.changed ?? null,
        fields: { ...state.fields, token: "" },
      };
    case "refused":
      return { ...state, saving: false, failure: event.message };
  }
}

/** What the choose step's list is asked with, or null on any other step: the token as a save would send it. */
export function channelsAsked(state: SlackFlowState): SlackAsking | null {
  if (state.step !== "where") return null;
  // The installation's token is the server's to open (#936): the browser never had it.
  if (state.installation !== null) return { installation_id: state.installation.id };
  // A saved connection's own token is the server's to open (#817): the browser never had it.
  if (state.editing !== null && !state.replacing) return { connection_id: state.editing.id };
  if (state.fields.token === "") return null;
  return { credential: slackIntegration({ token: state.fields.token, channel: "", floor: "info" }).credential };
}

/** The connection the confirm step would create, or null when this is no moment to create one. */
export function slackSubmission(state: SlackFlowState): NewSlack | null {
  const { token, channel, floor } = state.fields;
  if (state.editing !== null || state.step !== "confirm" || state.saving || floor === "") return null;
  const pasted = slackIntegration({ token, channel, floor });
  const body: NewSlack =
    state.installation === null ? pasted : { kind: "slack", config: pasted.config, installation_id: state.installation.id };
  return state.sameSpace === null ? body : { ...body, alongside_existing: true };
}

/**
 * What an edit's confirm step would send, and what it would say it did (#817, #580): only
 * what changed, the token only when it was replaced. An edit with no change in it is
 * returned too, so the step can say so; it is never sent.
 */
export function slackEditSubmission(state: SlackFlowState): Edit<SlackPatch> | null {
  const { token, channel, floor } = state.fields;
  if (state.editing === null || state.step !== "confirm" || state.saving || floor === "") return null;
  const edit = slackEdit(state.editing.config, { channel, floor, token: state.replacing ? token : "" });
  return state.sameSpace === null ? edit : { ...edit, patch: { ...edit.patch, alongside_existing: true } };
}

/**
 * Put `text` on the clipboard. False when the browser has no clipboard to write to or
 * refuses the write, which is the caller's cue to leave the text selected instead.
 */
export async function copyText(text: string, clipboard: Pick<Clipboard, "writeText"> | undefined): Promise<boolean> {
  if (clipboard === undefined) return false;
  try {
    await clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

const COPIED = "Copied. Paste it into Slack’s From a manifest box.";
const COPY_REFUSED = "This browser did not allow the copy. The manifest is selected: copy it with Ctrl+C or ⌘C.";

/** The manifest in its own scrolling box, with a button that copies exactly what the box shows. */
export function Manifest() {
  const box = useRef<HTMLPreElement>(null);
  const [said, setSaid] = useState<string | null>(null);

  const copy = async () => {
    const copied = await copyText(SLACK_MANIFEST_TEXT, typeof navigator === "undefined" ? undefined : navigator.clipboard);
    if (!copied && box.current !== null) window.getSelection()?.selectAllChildren(box.current);
    setSaid(copied ? COPIED : COPY_REFUSED);
  };

  return (
    <div className="step-flow__manifest">
      <pre ref={box} tabIndex={0} role="region" aria-label="Slack app manifest" data-testid="flow-slack-manifest">
        {SLACK_MANIFEST_TEXT}
      </pre>
      <div className="actions">
        <button type="button" className="btn btn--small" onClick={() => void copy()}>
          Copy manifest
        </button>
        <small className="field__hint" role="status" data-testid="flow-slack-copied">
          {said}
        </small>
      </div>
    </div>
  );
}

function Actions({ children }: { children: ReactNode }) {
  return <div className="actions">{children}</div>;
}

/**
 * The repair for a Slack app installed without `channels:read` (#605), where the edit is
 * chosen from: adding a scope to a manifest changes no token already issued, so the app is
 * updated from the current manifest, reinstalled, and its token pasted in. Saving is an
 * edit, which is checked again (#598); the notice clears once that check reads the
 * channel. Nothing is disconnected or re-created (#580).
 */
function GrantChannelsRead() {
  return (
    <section className="stack stack--tight" data-testid="grant-channels-read" aria-label="Add channels:read">
      <h4>Add channels:read</h4>
      <p className="muted">
        This app was installed without the channels:read permission, so Slack will not list its channels. Posting keeps working
        without it. To add it:
      </p>
      <ol className="step-flow__needs">
        <li>
          <strong>Update the app.</strong> Open{" "}
          <a href={SLACK_APPS} target="_blank" rel="noreferrer noopener">
            api.slack.com/apps
          </a>{" "}
          → your app → App Manifest. Replace it with the manifest below, which asks for {SLACK_MANIFEST_SCOPES.join(" and ")}, and save.
        </li>
        <li>
          <strong>Reinstall it.</strong> In the app: Install App → Reinstall to Workspace, and allow what it asks for.
        </li>
        <li>
          <strong>Paste its token.</strong> In the app: OAuth &amp; Permissions → Bot User OAuth Token. Copy it — Slack may give the same
          one — then choose Replace the token below, paste it, and save. The connection is checked again, and this clears once Slack
          confirms the permission.
        </li>
      </ol>
      <Manifest />
    </section>
  );
}

/** Where the invite goes, in Slack's own words: the manifest's bot name. */
const INVITE = "/invite @Torque Assist";

/** The choose step, from Slack's own list (#604): a public channel the app is in, or an ID typed. */
function ChooseChannel({
  state,
  dispatch,
  onward,
  stepLabel,
  back,
  cancel,
}: {
  state: SlackFlowState;
  dispatch: (event: SlackFlowEvent) => void;
  onward: (event: FormEvent) => void;
  stepLabel: string | undefined;
  back: ReactNode;
  cancel: ReactNode;
}) {
  const { channels, fields } = state;
  const again = (label: string) => (
    <button type="button" className="btn" disabled={channels === "loading"} onClick={() => dispatch({ type: "recheck" })}>
      {label}
    </button>
  );
  if (channels === "idle" || channels === "loading") {
    return (
      <>
        <p role="status">Reading the public channels this app can see…</p>
        <Actions>
          {back}
          {cancel}
        </Actions>
      </>
    );
  }
  const typed = (
    <SlackChannelField
      value={fields.channel}
      error={state.channelError}
      onType={(value) => dispatch({ type: "typed", field: "channel", value })}
      onLeave={() => dispatch({ type: "left", field: "channel" })}
    />
  );
  const actions = (primary: boolean) => (
    <Actions>
      {primary ? (
        <button type="submit" className="btn btn--primary">
          Continue
        </button>
      ) : null}
      {back}
      {cancel}
    </Actions>
  );
  if ("failed" in channels) {
    return (
      <form onSubmit={onward} className="stack stack--tight" aria-label={stepLabel}>
        <Notice tone="bad">{channels.failed}</Notice>
        <Actions>{again("Try again")}</Actions>
        <div className="settings-fields">{typed}</div>
        {actions(true)}
      </form>
    );
  }
  if (state.typingId || channels.items.length === 0) {
    return (
      <form onSubmit={onward} className="stack stack--tight" aria-label={stepLabel}>
        {channels.items.length === 0 ? (
          <Notice>Slack lists no public channels for this app. Give the channel’s ID instead.</Notice>
        ) : (
          <p className="muted">A private channel is not listed: give its ID, and invite the app to it first.</p>
        )}
        <div className="settings-fields">{typed}</div>
        {channels.items.length === 0 ? (
          <Actions>{again("Check again")}</Actions>
        ) : (
          <button type="button" className="btn btn--small" onClick={() => dispatch({ type: "typing-id", on: false })}>
            Choose from the list instead
          </button>
        )}
        {actions(true)}
      </form>
    );
  }
  const chosen = chosenChannel(state);
  return (
    <form onSubmit={onward} className="stack stack--tight" aria-label={stepLabel}>
      <div className="settings-fields">
        <JiraChoice
          id="slack-channel-choice"
          label="Channel"
          placeholder="Choose a channel"
          hint="Public channels, and whether Torque Assist is in each. A private channel is given by its ID."
          options={channels.items.map((channel) => ({
            value: channel.id,
            text: channel.is_member ? `#${channel.name}` : `#${channel.name} — Torque Assist is not in it`,
          }))}
          value={fields.channel}
          onChoose={(id) => dispatch({ type: "channel-chosen", id })}
        />
      </div>
      {chosen === null || chosen.is_member ? null : (
        <div data-testid="flow-slack-invite">
          <Notice>
            Torque Assist is not in #{chosen.name}, so it cannot post there. In Slack, open #{chosen.name} and type {INVITE}, then check
            again.
          </Notice>
          <Actions>{again("Check again")}</Actions>
        </div>
      )}
      <button type="button" className="btn btn--small" onClick={() => dispatch({ type: "typing-id", on: true })}>
        Give a private channel’s ID instead
      </button>
      <Actions>
        <button type="submit" className="btn btn--primary" disabled={chosen === null || !chosen.is_member}>
          Continue
        </button>
        {back}
        {cancel}
      </Actions>
    </form>
  );
}

export function SlackFlow({
  start,
  returned,
  held,
  editing,
  onCreate,
  onUpdate,
  onSendTest,
  onList,
  onEdit,
  onClose,
}: {
  /** The state the flow opens in. Only a test passes one, to render a step it cannot click to. */
  start?: SlackFlowState;
  /** What the callback said of an Add to Slack this person has just come back from (#937). */
  returned?: SlackInstallResult | null;
  /** The installation a return said is already held (#938), as the connections list knows it. */
  held?: HeldInstallation;
  /**
   * The saved connection this flow edits (#817), as the list holds it now: the flow opens
   * at its choose step, and what it says of the connection's health is kept current.
   */
  editing?: SlackRow;
  /** Save the connection. The row it answers with is what a test message is sent from. */
  onCreate: (body: NewIntegration) => Promise<IntegrationRow | void>;
  /** Save an edit: only what changed (#580). Editing without it, nothing can be saved. */
  onUpdate?: (patch: SlackPatch) => Promise<unknown>;
  /** Send one test message from a saved connection (#614). Without it the box is not offered. */
  onSendTest?: (id: string) => Promise<unknown>;
  /**
   * Read Slack's channel list for the choose step (#604). Without it the step asks for
   * the channel ID typed.
   */
  onList?: SlackLister;
  /** Open the edit of a connection the workspace already has (#762, #580, #817). */
  onEdit?: (id: string) => void;
  /** Leave the flow. Before the confirm step's save, nothing has been sent anywhere. */
  onClose: () => void;
}) {
  const [state, dispatch] = useReducer(
    slackFlowReducer,
    start ?? (editing === undefined ? returnedSlackFlow(returned ?? null) : editingSlackFlow(editing)),
  );
  const inFlight = useRef(false);
  const heading = useRef<HTMLHeadingElement>(null);
  const { step, fields } = state;
  const edit = slackEditSubmission(state);

  // Each step replaces the last one's controls, so the step's own heading takes focus.
  useEffect(() => {
    heading.current?.focus();
  }, [step]);

  // The choose step's read. Numbered, and the reducer takes an answer only to the number
  // it is waiting on: a list that comes back after the token has changed is of another app.
  const requests = useRef(0);
  const asking = channelsAsked(state);
  const wantChannels = onList !== undefined && asking !== null && state.channels === "idle";
  useEffect(() => {
    if (onList === undefined || asking === null || !wantChannels) return;
    const id = (requests.current += 1);
    dispatch({ type: "listing", id });
    onList.channels(asking).then(
      (answer) => dispatch({ type: "channels-listed", id, answer }),
      (refusal: unknown) => dispatch({ type: "listing-failed", id, message: messageOf(refusal) }),
    );
    // `asking` is read at the moment the list is wanted, and a change to it makes one wanted again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantChannels]);

  const onward = (event: FormEvent) => {
    event.preventDefault();
    dispatch({ type: "continue" });
  };

  const connect = async () => {
    const body = slackSubmission(state);
    const changed = edit === null || edit.changes.length === 0 || onUpdate === undefined ? null : edit;
    if ((body === null && changed === null) || inFlight.current) return;
    inFlight.current = true;
    dispatch({ type: "saving" });
    try {
      if (changed !== null) {
        await onUpdate?.(changed.patch);
        dispatch({ type: "saved", changed: changed.changes });
      } else if (body !== null) {
        // A box that was never offered was never ticked, whatever the state opened holding.
        const wanted = state.sendTest && onSendTest !== undefined;
        dispatch({ type: "saved", test: await connectThenTest(body, wanted, onCreate, onSendTest) });
      }
    } catch (refusal) {
      const existing = alreadyConnected(refusal);
      dispatch(existing === null ? { type: "refused", message: messageOf(refusal) } : { type: "already-connected", existing });
    } finally {
      inFlight.current = false;
    }
  };

  const withSaved = state.editing !== null && !state.replacing;
  // An installation's token is Slack's to replace (#938): its edit offers Reinstall where a
  // pasted token's offers Replace the token (#580).
  const installation = editing === undefined ? null : reinstallable(editing);
  const back = (
    <button type="button" className="btn" disabled={state.saving} onClick={() => dispatch({ type: "back" })}>
      {state.editing !== null && step === "sign-in" ? "Keep the saved token" : "Back"}
    </button>
  );
  // Editing opens at the choose step: before it is only the sign-in, redone to replace the token.
  // Back from Add to Slack opens there too, with nothing before it here (#937).
  const backFromWhere =
    state.installation !== null ? null : installation !== null ? (
      <a className="btn" href={reinstallHref(installation)}>
        Reinstall
      </a>
    ) : withSaved ? (
      <button type="button" className="btn" onClick={() => dispatch({ type: "replace-token" })}>
        Replace the token
      </button>
    ) : (
    back
  );
  const cancel = (
    <button type="button" className="btn" disabled={state.saving} onClick={onClose}>
      Cancel
    </button>
  );
  const steps = state.editing !== null ? SLACK_EDIT_STEPS : state.installation !== null ? SLACK_INSTALL_STEPS : SLACK_STEPS;
  const stepLabel = state.held !== null ? "Already added to Slack" : steps.find((each) => each.id === step)?.label;
  const title = (
    <h4 ref={heading} tabIndex={-1} className="step-flow__title">
      {stepLabel}
    </h4>
  );

  let body: ReactNode;
  if (step === "create-app" && state.held !== null) {
    // Nothing was written (#938): the explicit replace, or the installation as it is.
    body = (
      <>
        <Notice>{heldInWords(held ?? { name: null, connections: 0 })}</Notice>
        <Actions>
          <a className="btn btn--primary" href={reinstallHref(state.held.id)}>
            Reinstall Slack (replace)
          </a>
          <button type="button" className="btn" onClick={() => dispatch({ type: "use-held" })}>
            Choose a channel with it
          </button>
          {cancel}
        </Actions>
      </>
    );
  } else if (step === "create-app" && state.refusal === "conflict") {
    // Another workspace's Slack: said, and nothing offered that would go on with it.
    body = (
      <>
        <Notice tone="bad">{SLACK_INSTALL_REFUSED.conflict}</Notice>
        <Actions>{cancel}</Actions>
      </>
    );
  } else if (step === "create-app") {
    body = (
      <>
        {state.refusal === null ? null : <Notice tone="bad">{SLACK_INSTALL_REFUSED[state.refusal]}</Notice>}
        <div className="stack stack--tight" data-testid="flow-slack-add">
          <h5>Add to Slack</h5>
          <p className="muted">
            Recommended. Slack asks you to allow Torque Assist into your workspace, then brings you back here to choose a channel. There
            is no app to create and no token to copy.
          </p>
          <Actions>
            <a className="btn btn--primary" href={SLACK_INSTALL_PATH}>
              Add to Slack
            </a>
          </Actions>
        </div>
        <h5>Use your own Slack app</h5>
        <p className="muted">
          Conclusions and approval requests post to a channel through a Slack app you create in your own workspace. This manifest
          sets it up: a bot user, the {SLACK_MANIFEST_SCOPES.join(" and ")} scopes, a token that does not rotate, and nothing that
          sends anything back here.
        </p>
        <Manifest />
        <ol className="step-flow__needs">
          <li>
            <strong>Create the app.</strong> Open{" "}
            <a href={SLACK_APPS} target="_blank" rel="noreferrer noopener">
              api.slack.com/apps
            </a>{" "}
            → Create New App → From a manifest. Choose your workspace, paste the manifest, and create it.
          </li>
          <li>
            <strong>Install it.</strong> In the app: Install App → Install to your workspace, and allow what it asks for.
          </li>
          <li>
            <strong>Copy the Bot User OAuth Token.</strong> In the app: OAuth &amp; Permissions → Bot User OAuth Token. It starts with
            xoxb-, and the next step asks for it.
          </li>
          <li>
            <strong>Invite the app</strong> to the channel it should post to: in that channel, type /invite @Torque Assist.
          </li>
        </ol>
        <Actions>
          <button type="button" className="btn btn--primary" onClick={() => dispatch({ type: "continue" })}>
            Continue
          </button>
          {cancel}
        </Actions>
      </>
    );
  } else if (step === "where" && onList !== undefined) {
    body = <ChooseChannel state={state} dispatch={dispatch} onward={onward} stepLabel={stepLabel} back={backFromWhere} cancel={cancel} />;
  } else if (step === "sign-in" || step === "where" || step === "threshold") {
    body = (
      <form onSubmit={onward} className="stack stack--tight" aria-label={stepLabel}>
        <div className="settings-fields">
          {step === "sign-in" ? (
            <SlackTokenField
              value={fields.token}
              error={state.tokenError}
              onType={(value) => dispatch({ type: "typed", field: "token", value })}
              onLeave={() => dispatch({ type: "left", field: "token" })}
            />
          ) : step === "where" ? (
            <SlackChannelField
              value={fields.channel}
              error={state.channelError}
              onType={(value) => dispatch({ type: "typed", field: "channel", value })}
              onLeave={() => dispatch({ type: "left", field: "channel" })}
            />
          ) : (
            <SlackFloorField value={fields.floor} onChoose={(floor) => dispatch({ type: "floor-chosen", floor })} />
          )}
        </div>
        <Actions>
          <button type="submit" className="btn btn--primary">
            Continue
          </button>
          {step === "where" ? backFromWhere : back}
          {cancel}
        </Actions>
      </form>
    );
  } else if (step === "confirm") {
    const channel = slackChannelSent(fields.channel);
    const unchanged = edit !== null && edit.changes.length === 0;
    body = (
      <>
        {state.failure === null ? null : <Notice tone="bad">{state.failure} Your entries are kept.</Notice>}
        {state.sameSpace === null ? null : (
          <div className="stack stack--tight" data-testid="flow-slack-same-channel">
            <Notice tone="bad">{sameSpaceInWords(state.sameSpace)}</Notice>
            {onEdit === undefined ? null : (
              <Actions>
                <button type="button" className="btn btn--primary" onClick={() => onEdit(state.sameSpace?.id ?? "")}>
                  Edit that connection instead
                </button>
              </Actions>
            )}
          </div>
        )}
        <p data-testid="flow-slack-summary">
          Conclusions at <strong>{fields.floor === "" ? "" : SEVERITY_LABEL[fields.floor]}</strong> and above post to{" "}
          <strong>{channel}</strong>. {APPROVALS_ALWAYS_POST}
        </p>
        <p className="muted">
          {state.installation !== null
            ? "It posts as the Torque Assist app you added to Slack. Its token stays with Torque Assist and is never shown."
            : withSaved
              ? "The saved token is kept."
              : `The token is stored encrypted when you ${state.editing === null ? "connect" : "save"}, and is never shown again.`}
        </p>
        {edit === null ? null : unchanged ? (
          <p className="muted" data-testid="flow-slack-changes">
            {NOTHING_CHANGED}
          </p>
        ) : (
          <p data-testid="flow-slack-changes">Saving: {edit.changes.map((change) => `${change}.`).join(" ")}</p>
        )}
        {onSendTest === undefined || state.editing !== null ? null : (
          <div className="step-flow__option">
            <label htmlFor="flow-slack-send-test">
              <input
                type="checkbox"
                id="flow-slack-send-test"
                checked={state.sendTest}
                disabled={state.saving}
                onChange={(event) => dispatch({ type: "test-toggled", on: event.target.checked })}
              />{" "}
              Send a test message
            </label>
            <p className="muted">This posts one message, marked [Test], to {channel}, so you can see it arrive.</p>
          </div>
        )}
        <Actions>
          <button
            type="button"
            className={state.sameSpace === null ? "btn btn--primary" : "btn"}
            disabled={state.saving || unchanged}
            aria-busy={state.saving}
            onClick={() => void connect()}
          >
            {state.editing === null
              ? state.saving
                ? "Connecting…"
                : state.sameSpace === null
                  ? "Connect Slack"
                  : "Connect a second one anyway"
              : state.saving
                ? "Saving…"
                : state.sameSpace === null
                  ? "Save changes"
                  : "Save it anyway"}
          </button>
          {back}
          {cancel}
        </Actions>
      </>
    );
  } else {
    body = (
      <>
        {state.changed === null ? <Notice tone="good">{SLACK_CONNECTED}</Notice> : <SavedChanges changes={state.changed} />}
        {state.test === "not-asked" ? null : state.test === "asked" ? (
          <p data-testid="flow-slack-test-asked">
            A test message is on its way to {slackChannelSent(fields.channel)}. Its result shows under Health in the list above.
          </p>
        ) : (
          <Notice tone="bad">The test message was not sent: {state.test.failed}</Notice>
        )}
        <p className="muted">It is in the list above, with its health. Edit it there if Slack starts refusing it.</p>
        <Actions>
          <button type="button" className="btn btn--primary" onClick={onClose}>
            Done
          </button>
        </Actions>
      </>
    );
  }

  const flowTitle = state.editing === null ? "Connect Slack" : "Edit Slack";
  return (
    <StepFlow title={flowTitle} testId={state.editing === null ? "flow-slack" : "flow-slack-edit"} steps={steps} current={step}>
      {editing === undefined ? null : <EditingLine row={editing} />}
      {title}
      {editing !== undefined && step === "where" && missingGrant(editing) !== null ? (
        installation === null ? (
          <GrantChannelsRead />
        ) : (
          <Notice>{grantInWords(editing, true)}</Notice>
        )
      ) : null}
      {body}
    </StepFlow>
  );
}
